import React, { useState, useEffect } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useOrganization,
  useEditOrganization,
  useDeleteOrganization,
} from "../../hooks/useOrganization";
import { usePermission } from "../../hooks/usePermission";
import { useCountry, useCountryNameById } from "../../hooks/useCountry";
import { useCityNameById, useCountryCities } from "../../hooks/useCity";
import DataTable from "../shared/DataTable";
import {
  EditOrganizationsPermission,
  DeleteOrganizationsPermission,
} from "../../constants/permissions";

const CountryNameCell = ({ countryId }) => {
  const { data: countryName } = useCountryNameById(countryId);
  return countryName || "";
};

const CityNameCell = ({ cityId }) => {
  const { data: cityName } = useCityNameById(cityId);
  return cityName || "";
};

const OrganizationTable = () => {
  const { data: organizations, isLoading } = useOrganization();
  const editOrganization = useEditOrganization();
  const deleteOrganization = useDeleteOrganization();
  const { data: countries } = useCountry();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    organization_id: "",
    email: "",
    phone_number: "",
    contact_name: "",
    registration_number: "",
    APO_number: "",
    city_id: "",
    country_id: "",
  });
  const { data: cities, refetch: refetchCities } = useCountryCities(
    editFormData?.country_id
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);

  const { data: canEditOrganization } = usePermission({
    permissionName: EditOrganizationsPermission,
  });

  const { data: canDeleteOrganization } = usePermission({
    permissionName: DeleteOrganizationsPermission,
  });

  useEffect(() => {
    if (editFormData.country_id) {
      refetchCities();
    }
  }, [editFormData.country_id, refetchCities]);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionId(null);
  };

  const handleEditClick = (organization) => {
    setSelectedOrganization(organization);
    setEditFormData({
      name: organization.name,
      organization_id: organization.organization_id,
      email: organization.email,
      phone_number: organization.phone_number,
      contact_name: organization.contact_name,
      registration_number: organization.registration_number,
      APO_number: organization.APO_number,
      city_id: organization.city_id,
      country_id: organization.country_id,
      updated_at: new Date().toISOString(),
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedOrganization(null);
  };

  const handleEditSubmit = async () => {
    try {
      await editOrganization.mutateAsync({
        id: selectedOrganization.id,
        data: editFormData,
      });
      handleEditClose();
    } catch (error) {
      console.error("Error editing organization:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this organization?")) {
      try {
        await deleteOrganization.mutateAsync(id);
        handleMenuClose();
      } catch (error) {
        console.error("Error deleting organization:", error);
      }
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Organization ID",
      selector: (row) => row.organization_id,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone Number",
      selector: (row) => row.phone_number,
      sortable: true,
    },
    {
      name: "Contact Name",
      selector: (row) => row.contact_name,
      sortable: true,
    },
    {
      name: "Registration Number",
      selector: (row) => row.registration_number,
      sortable: true,
      width: "190px",
    },
    {
      name: "APO Number",
      selector: (row) => row.APO_number,
      sortable: true,
    },
    {
      name: "Country",
      selector: (row) => {
        return <CountryNameCell countryId={row.country_id} />;
      },
      sortable: true,
    },
    {
      name: "City",
      selector: (row) => {
        return <CityNameCell cityId={row.city_id} />;
      },
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
      width: "100px",
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={organizations}
        searchFields={[
          "name",
          "organization_id",
          "email",
          "phone_number",
          "contact_name",
          "registration_number",
          "APO_number",
        ]}
        searchPlaceholder="Search organizations..."
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canEditOrganization && (
          <MenuItem
            onClick={() =>
              handleEditClick(
                organizations.find((org) => org.id === selectedActionId)
              )
            }
          >
            <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
            <h6 className="text-blueColor">Edit</h6>
          </MenuItem>
        )}
        {canDeleteOrganization && (
          <MenuItem
            data-track="delete-organization-button"
            data-module="Organizations"
            onClick={() => handleDelete(selectedActionId)}
          >
            <i className="fa-solid fa-trash text-red-600 mr-2" />
            <h6 className="text-blueColor">Delete</h6>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-edit text-xl mx-2"></i>
          <span className="text-xl font-semibold">Edit Organization</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-2 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Name
              </label>
              <input
                name="name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Organization ID
              </label>
              <input
                name="organization_id"
                value={editFormData.organization_id}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    organization_id: e.target.value,
                  })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <input
                name="email"
                value={editFormData.email}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, email: e.target.value })
                }
                type="email"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number
              </label>
              <input
                name="phone_number"
                value={editFormData.phone_number}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    phone_number: e.target.value,
                  })
                }
                type="tel"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Contact Name
              </label>
              <input
                name="contact_name"
                value={editFormData.contact_name}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    contact_name: e.target.value,
                  })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Registration Number
              </label>
              <input
                name="registration_number"
                value={editFormData.registration_number}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    registration_number: e.target.value,
                  })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                APO Number
              </label>
              <input
                name="APO_number"
                value={editFormData.APO_number}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    APO_number: e.target.value,
                  })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country
              </label>
              <select
                name="country_id"
                value={editFormData.country_id}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    country_id: e.target.value,
                    city_id: "",
                  })
                }
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="">Select Country</option>
                {countries?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <select
                name="city_id"
                value={editFormData.city_id}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, city_id: e.target.value })
                }
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                disabled={!editFormData.country_id}
              >
                <option value="">Select City</option>
                {cities?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={handleEditClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              data-track="edit-organization-button"
              data-module="Organizations"
              onClick={handleEditSubmit}
              className="px-4 py-2 bg-blueColor text-white rounded"
            >
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrganizationTable;
